import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";
import { CalendarCheck, Video, Phone, Users, Check, Clock, Loader2 } from "lucide-react";
import { services } from "@/lib/services-data";
import { submitBooking, getAvailableSlots } from "@/lib/api/intake.functions";
import { Disclaimer } from "@/components/site/Disclaimer";
import {
  NonReservedNotice,
  NON_RESERVED_STATEMENT,
  NON_RESERVED_ACKNOWLEDGEMENT,
} from "@/components/site/NonReservedNotice";

const searchSchema = z.object({
  service: z.string().optional(),
});

export const Route = createFileRoute("/book")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Book a Consultation | Nkadimeng Danny Legal & Compliance" },
      {
        name: "description",
        content:
          "Book a physical, telephone, Microsoft Teams or Zoom consultation with Nkadimeng Danny Legal & Compliance Support.",
      },
      { property: "og:title", content: "Book a Legal Consultation" },
      {
        property: "og:description",
        content:
          "Choose your service, date and consultation type. We'll confirm by email.",
      },
      { property: "og:url", content: "https://nkadimengdannylcs.com/book" },
    ],
    links: [{ rel: "canonical", href: "https://nkadimengdannylcs.com/book" }],
  }),
  component: BookPage,
});

// Mon–Fri 09:00–17:00 in 30-minute increments
const timeslots = (() => {
  const out: string[] = [];
  for (let h = 9; h < 17; h++) {
    out.push(`${String(h).padStart(2, "0")}:00`);
    out.push(`${String(h).padStart(2, "0")}:30`);
  }
  return out;
})();

const consultTypes = [
  { value: "physical", label: "In-person", icon: Users },
  { value: "telephone", label: "Telephone", icon: Phone },
  { value: "teams", label: "MS Teams", icon: Video },
  { value: "zoom", label: "Zoom", icon: Video },
] as const;

function BookPage() {
  const { service: preselected } = Route.useSearch();
  const book = useServerFn(submitBooking);
  const fetchSlots = useServerFn(getAvailableSlots);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [consultType, setConsultType] = useState<typeof consultTypes[number]["value"]>("teams");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);

  const today = new Date().toISOString().slice(0, 10);

  // Disallow weekends — Mon–Fri only
  const isWeekend = useMemo(() => {
    if (!date) return false;
    const d = new Date(date + "T00:00:00");
    const day = d.getDay();
    return day === 0 || day === 6;
  }, [date]);

  const { data: avail, isFetching: loadingSlots } = useQuery({
    queryKey: ["available-slots", date],
    queryFn: () => fetchSlots({ data: { date } }),
    enabled: !!date && !isWeekend,
    staleTime: 30_000,
  });
  const booked = new Set(avail?.booked ?? []);

  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-20">
          <span className="gold-rule" />
          <h1 className="font-display text-4xl text-navy md:text-5xl">
            Book a consultation
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Choose your service, preferred date and how you'd like to meet.
            Once you submit, your invoice will appear instantly — you can send
            it to yourself via WhatsApp or email and pay by EFT.
          </p>
          <div className="mt-6 max-w-3xl space-y-4">
            <NonReservedNotice />
            <Disclaimer variant="compact" />
          </div>
        </div>
      </section>

      <section className="container-narrow py-12">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!date || isWeekend) {
              toast.error("Pick a weekday between Monday and Friday.");
              return;
            }
            if (!time) {
              toast.error("Please choose an available time slot.");
              return;
            }
            if (!acknowledged) {
              toast.error(
                "Please confirm you understand our non-reserved advice scope.",
              );
              return;
            }
            const f = new FormData(e.currentTarget);
            setLoading(true);
            try {
              const res = await book({
                data: {
                  full_name: String(f.get("full_name") || ""),
                  email: String(f.get("email") || ""),
                  phone: String(f.get("phone") || ""),
                  service: String(f.get("service") || ""),
                  consultation_type: consultType,
                  preferred_date: date,
                  preferred_time: time,
                  message: String(f.get("message") || ""),
                },
              });
              toast.success("Booking received — opening your invoice");
              navigate({ to: "/invoice/$id", params: { id: res.id } });
            } catch (err) {
              toast.error(
                err instanceof Error ? err.message : "Could not submit booking",
              );
            } finally {
              setLoading(false);
            }
          }}
          className="mx-auto max-w-3xl rounded-2xl border border-border bg-card p-6 shadow-soft md:p-10"
        >
          {/* Consultation type */}
          <div>
            <span className="text-sm font-medium text-navy" id="consult-type-label">
              Consultation type
            </span>
            <div role="group" aria-labelledby="consult-type-label" className="mt-3 grid gap-3 sm:grid-cols-4">
              {consultTypes.map((t) => {
                const active = consultType === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setConsultType(t.value)}
                    className={
                      "flex flex-col items-center gap-1.5 rounded-lg border p-3 text-sm transition " +
                      (active
                        ? "border-gold bg-gold/10 text-navy"
                        : "border-border bg-background text-muted-foreground hover:border-navy/30")
                    }
                  >
                    <t.icon className="h-5 w-5" />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Field name="full_name" label="Full name" required />
            <Field name="email" type="email" label="Email" required />
            <Field name="phone" label="Phone" required />
            <Select
              name="service"
              label="Service"
              defaultValue={preselected}
              required
              options={[
                { value: "", label: "Select a service" },
                ...services.map((s) => ({ value: s.slug, label: s.title })),
              ]}
            />
          </div>

          {/* Step indicator */}
          <ol className="mt-8 grid gap-3 sm:grid-cols-3 text-xs">
            <Step n={1} label="Pick a weekday" done={!!date && !isWeekend} />
            <Step n={2} label="Choose a free slot" done={!!time} />
            <Step n={3} label="Submit & get instant confirmation" done={false} />
          </ol>

          <div className="mt-4">
            <label htmlFor="book-preferred-date" className="text-sm font-medium text-navy">
              Preferred date <span className="text-destructive">*</span>
            </label>
            <input
              id="book-preferred-date"
              name="preferred_date"
              type="date"
              required
              min={today}
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setTime("");
              }}
              className="mt-1.5 w-full max-w-xs rounded-md border border-input bg-background px-3 py-2.5 text-base md:text-sm focus:border-gold focus:outline-none"
            />
            {isWeekend && (
              <p className="mt-2 text-xs text-amber-700">
                We only take consultations Monday to Friday. Please pick a weekday.
              </p>
            )}
          </div>

          <div className="mt-4">
            <span className="flex items-center gap-2 text-sm font-medium text-navy">
              Available time slots
              {loadingSlots && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
            </span>
            <p className="mt-1 text-xs text-muted-foreground">
              Mon–Fri, 09:00–17:00 SAST · 30-minute slots. Greyed out slots are already booked.
            </p>
            <input type="hidden" name="preferred_time" value={time} />
            {!date || isWeekend ? (
              <p className="mt-3 rounded-md border border-dashed border-border bg-muted/40 px-3 py-4 text-xs text-muted-foreground">
                <Clock className="mr-1 inline h-3.5 w-3.5" /> Pick a weekday above to see live availability.
              </p>
            ) : (
              <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
                {timeslots.map((t) => {
                  const isBooked = booked.has(t);
                  const active = time === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      disabled={isBooked || loadingSlots}
                      onClick={() => setTime(t)}
                      className={
                        "rounded-md border px-2 py-2 text-xs font-medium transition " +
                        (isBooked
                          ? "border-border bg-muted text-muted-foreground line-through cursor-not-allowed"
                          : active
                            ? "border-gold bg-gold/15 text-navy"
                            : "border-border bg-background text-foreground hover:border-navy/40")
                      }
                    >
                      {active && <Check className="mr-1 inline h-3 w-3" />}
                      {t}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-4">
            <label htmlFor="book-message" className="text-sm font-medium text-navy">
              Briefly describe your matter
            </label>
            <textarea
              id="book-message"
              name="message"
              rows={5}
              className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2.5 text-base md:text-sm focus:border-gold focus:outline-none"
              placeholder="Optional — a short summary helps us prepare."
            />
          </div>


          <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-lg border border-gold/40 bg-gold/5 p-4 text-sm leading-relaxed text-foreground">
            <input
              type="checkbox"
              required
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--navy))]"
            />
            <span>{NON_RESERVED_ACKNOWLEDGEMENT}</span>
          </label>

          <button
            type="submit"
            disabled={loading || !acknowledged}
            className="mt-6 inline-flex w-full md:w-auto items-center justify-center gap-2 rounded-md bg-navy px-6 py-3 text-base md:text-sm font-semibold text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
          >
            <CalendarCheck className="h-4 w-4" />
            {loading ? "Submitting..." : "Submit booking"}
          </button>
          <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
            By submitting you confirm that you have read our{" "}
            <a href="/disclaimer" className="underline hover:text-navy">disclaimer</a>{" "}
            and understand that {NON_RESERVED_STATEMENT} Your information is
            handled confidentially in line with POPIA.
          </p>
        </form>
      </section>
    </>
  );
}

function Field({
  name,
  label,
  type = "text",
  required,
  min,
}: {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  min?: string;
}) {
  const inputMode =
    type === "email" ? "email" : name === "phone" || type === "tel" ? "tel" : undefined;
  const autoComplete =
    name === "full_name"
      ? "name"
      : type === "email"
        ? "email"
        : name === "phone" || type === "tel"
          ? "tel"
          : undefined;
  return (
    <div>
      <label htmlFor={`book-${name}`} className="text-sm font-medium text-navy">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      <input
        id={`book-${name}`}
        name={name}
        type={name === "phone" ? "tel" : type}
        required={required}
        min={min}
        inputMode={inputMode as any}
        autoComplete={autoComplete}
        className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2.5 text-base md:text-sm focus:border-gold focus:outline-none"
      />
    </div>
  );
}

function Select({
  name,
  label,
  options,
  required,
  defaultValue,
}: {
  name: string;
  label: string;
  options: { value: string; label: string }[];
  required?: boolean;
  defaultValue?: string;
}) {
  return (
    <div>
      <label htmlFor={`book-${name}`} className="text-sm font-medium text-navy">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      <select
        id={`book-${name}`}
        name={name}
        required={required}
        defaultValue={defaultValue}
        className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2.5 text-base md:text-sm focus:border-gold focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Step({ n, label, done }: { n: number; label: string; done: boolean }) {
  return (
    <li
      className={
        "flex items-center gap-2 rounded-md border px-3 py-2 " +
        (done
          ? "border-emerald-300 bg-emerald-50 text-emerald-900"
          : "border-border bg-muted/40 text-muted-foreground")
      }
    >
      <span
        className={
          "inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold " +
          (done ? "bg-emerald-600 text-white" : "bg-background border border-border")
        }
      >
        {done ? <Check className="h-3 w-3" /> : n}
      </span>
      {label}
    </li>
  );
}
